/* eslint-disable max-lines-per-function */

import { NgClass } from '@angular/common';
import {
  Component,
  ElementRef,
  inject,
  input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';

import {
  NgxOlLayerVectorTileComponent,
  NgxOlMapComponent,
} from '@sungissia/ol-ngx';

import Control from 'ol/control/Control';
import { platformModifierKeyOnly } from 'ol/events/condition';
import { FeatureLike } from 'ol/Feature';
import DragBox from 'ol/interaction/DragBox';
import VectorTileLayer from 'ol/layer/VectorTile';
import MapBrowserEvent from 'ol/MapBrowserEvent';
import RenderFeature, { toFeature } from 'ol/render/Feature';

import { SeedingSlot, SeedingStore } from '@modules/seeding/seeding.store';

import { MapStore } from '../map.store';

@Component({
  imports: [MatIconModule, NgClass],
  selector: 'norupe-selector-control',
  styles: ``,
  templateUrl: './selector-control.component.html',
})
export class SelectorControlComponent implements OnInit, OnDestroy {
  map = input.required<NgxOlMapComponent>();
  territoriesLayer = input.required<NgxOlLayerVectorTileComponent>();
  selectedTerritoriesLayer = input.required<VectorTileLayer>();
  context = input<'territory' | 'seeding'>('territory');

  readonly mapStore = inject(MapStore);
  readonly elementRef = inject(ElementRef);
  readonly seedingStore = inject(SeedingStore);

  selectedControl = 0;
  dragBox?: DragBox;
  selectorControl = new Control({
    element: this.elementRef.nativeElement,
  });
  private clickHandler?: (event: MapBrowserEvent) => void;
  controls = [
    {
      action: () => {
        this.clickToSelect();
      },
      icon: 'ads_click',
      name: 'Atzīmēt ar klikšķi',
    },
    {
      action: () => {
        this.drawBoxToSelect();
      },
      icon: 'square',
      name: 'Iezīmēt teritoriju',
    },
    {
      action: () => {
        this.deleteSelection();
      },
      icon: 'delete',
      name: 'Notīrīt',
    },
  ];

  ngOnInit() {
    this.map().getInstance().addControl(this.selectorControl);
    this.onSelect(0);
  }
  onSelect(id: number) {
    this.selectedControl = id;
    this.clearAllInteractions();
    this.controls[id].action();
  }

  clearAllInteractions() {
    if (this.dragBox) {
      void this.map().getInstance().removeInteraction(this.dragBox);
    }
    if (this.clickHandler) {
      this.map().getInstance().un('singleclick', this.clickHandler);
    }
  }

  drawBoxToSelect() {
    this.dragBox = new DragBox({
      //need to hold ctrl and draw
      condition: platformModifierKeyOnly,
    });
    this.map().getInstance().addInteraction(this.dragBox);

    this.dragBox.on('boxend', () => {
      const extent = this.dragBox!.getGeometry().getExtent();

      const featuresInExtent = this.territoriesLayer()
        .getInstance()
        .getFeaturesInExtent(extent)
        .filter(
          (feature: RenderFeature) =>
            feature.getProperties()['id'] !==
            '80b8a334-a627-4b6b-b33e-5e639157e9a0',
        );

      //rotation fix taken from https://github.com/openlayers/openlayers/pull/9882/files
      const rotation = this.map().getInstance().getView().getRotation();
      const oblique = rotation % (Math.PI / 2) !== 0;
      const anchor = [0, 0];

      //rotate the box before so no need to rotate it for each feature
      let rotatedBoxExtent = extent;
      if (oblique) {
        const rotatedBox = this.dragBox!.getGeometry();
        rotatedBox.rotate(-rotation, anchor);
        rotatedBoxExtent = rotatedBox.getExtent();
      }

      if (this.context() === 'seeding') {
        const seedingSlots = featuresInExtent
          .map((mvtFeature: RenderFeature) => toFeature(mvtFeature))
          .filter((feature) => {
            const geometry = feature.getGeometry();
            if (!geometry) {
              return false;
            }

            if (oblique) {
              const geometryClone = geometry.clone();
              geometryClone.rotate(-rotation, anchor);
              if (!geometryClone.intersectsExtent(rotatedBoxExtent)) {
                return false;
              }
            } else {
              if (!geometry.intersectsExtent(extent)) {
                return false;
              }
            }

            //only aalow to select slots that are empty or not empty if the option is set
            const isEmpty = feature.get('is_empty') as boolean;
            if (this.seedingStore.slotSelectOption() === 'empty' && !isEmpty) {
              return false;
            }
            if (this.seedingStore.slotSelectOption() === 'full' && isEmpty) {
              return false;
            }

            return true;
          })
          .map(
            (feature): SeedingSlot => ({
              id: feature.get('id') as string,
              isEmpty: feature.get('is_empty') as boolean,
              palletId: feature.get('pallet_id') as number,
              seedingId: feature.get('seeding_id') as number,
            }),
          )
          .filter(
            (slot, index, arr) =>
              arr.findIndex((s) => s.id === slot.id) === index,
          );

        this.seedingStore.updateSelectedSlots([
          ...this.seedingStore.selectedSeedingSlots(),
          ...seedingSlots.filter(
            (slot) => !this.seedingStore.selectedSeedingSlotsIds().has(slot.id),
          ),
        ]);
      } else {
        const territoriesIds = featuresInExtent
          .map((mvtFeature: RenderFeature) => toFeature(mvtFeature))
          .filter((feature) => {
            const geometry = feature.getGeometry();
            if (!geometry) {
              return false;
            }

            if (oblique) {
              const geometryClone = geometry.clone();
              geometryClone.rotate(-rotation, anchor);
              return geometryClone.intersectsExtent(rotatedBoxExtent);
            }
            return geometry.intersectsExtent(extent);
          })
          .map((feature) => feature.get('id') as string);

        //get rid of duplicates
        const territories = new Set([
          ...this.mapStore.selectedTerritoriesIds(),
          ...territoriesIds,
        ]);

        this.mapStore.updateSelectedTerritories(Array.from(territories));
      }
    });
  }
  clickToSelect() {
    this.clickHandler = (event: MapBrowserEvent) => {
      //ignore mapevents without holding control
      if (!platformModifierKeyOnly(event)) {
        return;
      }
      const clickedFeatures = this.map()
        .getInstance()
        .getFeaturesAtPixel(event.pixel, {
          //only get the territories layer
          layerFilter: (layer) =>
            layer === this.territoriesLayer().getInstance(),
        })
        .filter(
          (renderFeature: FeatureLike) =>
            renderFeature.get('id') !== '80b8a334-a627-4b6b-b33e-5e639157e9a0',
        ) as RenderFeature[];

      if (clickedFeatures.length === 0) {
        return;
      }

      const featureId = clickedFeatures[0].get('id') as string;

      if (this.context() === 'seeding') {
        const isEmpty = clickedFeatures[0].get('is_empty') as boolean;

        if (this.seedingStore.slotSelectOption() === 'empty' && !isEmpty) {
          return;
        }
        if (this.seedingStore.slotSelectOption() === 'full' && isEmpty) {
          return;
        }

        if (this.seedingStore.selectedSeedingSlotsIds().has(featureId)) {
          const newSelection = this.seedingStore
            .selectedSeedingSlots()
            .filter((slot) => slot.id !== featureId);
          this.seedingStore.updateSelectedSlots(newSelection);
        } else {
          const newSlot: SeedingSlot = {
            id: featureId,
            isEmpty: clickedFeatures[0].get('is_empty') as boolean,
            palletId: clickedFeatures[0].get('pallet_id') as number,
            seedingId: clickedFeatures[0].get('seeding_id') as number,
          };
          this.seedingStore.updateSelectedSlots([
            ...this.seedingStore.selectedSeedingSlots(),
            newSlot,
          ]);
        }
      } else {
        this.mapStore.toggleSelectedTerritory(featureId);
      }
    };

    this.map().getInstance().on('singleclick', this.clickHandler);
  }

  deleteSelection() {
    if (this.context() === 'seeding') {
      this.seedingStore.updateSelectedSlots([]);
    } else {
      this.mapStore.updateSelectedTerritories([]);
    }
    this.onSelect(0);
  }

  ngOnDestroy() {
    this.clearAllInteractions();
    void this.map().getInstance().removeControl(this.selectorControl);
  }
}
